import React, { useState } from "react";
import { View, Text, Pressable, StyleSheet } from "react-native";
import {
  COLORS,
  COMMON_STYLES,
  TYPOGRAPHY,
  BG_STYLES,
  VARIANTS_STYLES,
} from "@dehaat/dds";

export interface CalendarGridProps {
  onDateSelected: (date: Date | null) => void;
}

export function CalendarGrid({ onDateSelected }: CalendarGridProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const CELL_SIZE = 36;
  const styles = createStyles(CELL_SIZE);

  const addMonths = (date: Date, amount: number): Date => {
    const d = new Date(date);
    d.setMonth(d.getMonth() + amount);
    return d;
  };

  // useEffect(() => {
  //     onDateSelected(selectedDate)
  // }, [onDateSelected, selectedDate])

  const isSameDay = (d1: Date, d2: Date): boolean =>
    d1.toDateString() === d2.toDateString();

  const startOfMonth = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), 1);

  const endOfMonth = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0);

  const getCalendarDates = (monthDate: Date): (Date | null)[] => {
    const start = startOfMonth(monthDate);
    const end = endOfMonth(monthDate);
    const dates: (Date | null)[] = [];

    const startDay = start.getDay(); // 0 (Sun) to 6 (Sat)

    // Add placeholders before 1st day
    for (let i = 0; i < startDay; i++) {
      dates.push(null);
    }

    // Add actual days
    for (let d = 1; d <= end.getDate(); d++) {
      dates.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), d));
    }

    // Fill last row
    while (dates.length % 7 !== 0) {
      dates.push(null);
    }

    return dates;
  };

  const days = getCalendarDates(currentMonth);

  return (
    <View
      style={[
        {
          maxWidth: 400,
          margin: "auto",
          borderRadius: 16,
          backgroundColor: "#F5F8FA",
        },
        COMMON_STYLES.pv1,
      ]}
    >
      {/* Month Header */}
      <View style={styles.monthRow}>
        <Pressable
          style={styles.arrowBtn}
          onPress={() => setCurrentMonth(addMonths(currentMonth, -1))}
        >
          <Text style={styles.arrowIcon}>{"<"}</Text>
        </Pressable>
        <Text style={styles.monthText}>
          {currentMonth.toLocaleDateString("en-GB", {
            month: "long",
            year: "numeric",
          })}
        </Text>
        <Pressable
          style={styles.arrowBtn}
          onPress={() => setCurrentMonth(addMonths(currentMonth, 1))}
        >
          <Text style={styles.arrowIcon}>{">"}</Text>
        </Pressable>
      </View>

      {/* Week Days */}
      <View style={styles.weekHeader}>
        {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
          <Text key={d} style={styles.weekDay}>
            {d}
          </Text>
        ))}
      </View>

      {/* Date Grid */}
      <View style={styles.grid}>
        {Array.from({ length: Math.ceil(days.length / 7) }, (_, weekIndex) => {
          const week = days.slice(weekIndex * 7, (weekIndex + 1) * 7);
          return (
            <View key={weekIndex} style={styles.weekRow}>
              {week.map((day, dayIndex) => {
                if (!day) {
                  return <View key={dayIndex} style={styles.emptyDayCell} />;
                }

                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const dayToCompare = new Date(day);
                dayToCompare.setHours(0, 0, 0, 0);

                const isSelected = selectedDate && isSameDay(selectedDate, day);
                const isFuture = dayToCompare.getTime() > today.getTime();

                return (
                  <Pressable
                    key={dayIndex}
                    style={[
                      styles.dayCell,
                      isSelected && styles.selectedDay,
                      isFuture && styles.disabledDayCell,
                    ]}
                    onPress={() => {
                      if (!isFuture) {
                        setSelectedDate(day);
                        onDateSelected(day);
                      }
                    }}
                  >
                    <Text
                      style={[
                        styles.dayText,
                        isSelected && styles.selectedDayText,
                      ]}
                    >
                      {day.getDate()}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          );
        })}
      </View>
    </View>
  );
}

const createStyles = (CELL_SIZE: number) =>
  StyleSheet.create({
    monthRow: {
      ...COMMON_STYLES.row,
      ...COMMON_STYLES.alignItemsCenter,
      ...COMMON_STYLES.justifyBetween,
      ...COMMON_STYLES["ph3/2"],
      ...COMMON_STYLES["pv1/2"],
    },
    monthText: {
      ...TYPOGRAPHY.TEXT1,
      ...VARIANTS_STYLES.SEMI_EMPHASIZED,
      color: COLORS.primary100,
    },
    arrowBtn: {
      width: 32,
      height: 32,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: COLORS.neutral40,
      ...COMMON_STYLES.justifyCenter,
      ...COMMON_STYLES.alignItemsCenter,
    },
    arrowIcon: {
      fontSize: 16,
      color: COLORS.success100,
    },
    weekHeader: {
      ...COMMON_STYLES.row,
      justifyContent: "space-around",
      paddingVertical: 8,
    },
    weekDay: {
      display: "flex",
      width: CELL_SIZE,
      height: CELL_SIZE,
      borderRadius: CELL_SIZE / 2,
      ...COMMON_STYLES.justifyCenter,
      ...COMMON_STYLES.alignItemsCenter,
      ...TYPOGRAPHY.TEXT2,
      ...VARIANTS_STYLES.BASE,
      color: "#1F9954",
      backgroundColor: "#fff",
    },
    grid: {
      flexDirection: "column",
      ...COMMON_STYLES.alignItemsCenter,
    },
    weekRow: {
      ...COMMON_STYLES.row,
      ...COMMON_STYLES.fullWidth,
      ...COMMON_STYLES["mb1/4"],
    },
    dayCell: {
      width: CELL_SIZE,
      height: CELL_SIZE,
      borderRadius: CELL_SIZE / 2,
      ...COMMON_STYLES.justifyCenter,
      ...COMMON_STYLES.alignItemsCenter,
      marginHorizontal: 6,
      marginVertical: 2,
      backgroundColor: "#fff",
    },
    selectedDay: {
      ...BG_STYLES.bgPrimary100,
      borderColor: "#00662C",
      borderWidth: 1,
    },
    dayText: {
      ...TYPOGRAPHY.TEXT2,
      ...VARIANTS_STYLES.BASE,
      color: COLORS.neutral90,
    },
    selectedDayText: {
      ...TYPOGRAPHY.TEXT2,
      ...VARIANTS_STYLES.BASE,
      color: COLORS.white,
    },
    emptyDayCell: {
      width: CELL_SIZE,
      height: CELL_SIZE,
      marginHorizontal: 6,
      marginVertical: 2,
      backgroundColor: "transparent",
    },
    disabledDayCell: {
      backgroundColor: COLORS.neutral10,
      cursor: "auto",
    },
  });
